'use client';

import dayjs from 'dayjs';
import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import { zhCN } from 'react-day-picker/locale';

import { useControl } from 'src/hooks';
import { cn } from 'src/lib/utils';

import { Input } from '../input';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const DateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

interface DateTimePickerProps {
  defaultValue?: Date;
  value?: Date;
  onChange?: (value: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  clearable?: boolean;
  captionLayout?: 'dropdown' | 'dropdown-years' | 'label' | 'dropdown-months' | undefined;
  disabled?: boolean | ((date: Date) => boolean) | Date[];
}

export function DateTimePicker({
  defaultValue,
  value,
  onChange,
  placeholder,
  className,
  clearable,
  captionLayout = 'dropdown-years',
  disabled,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = useControl(defaultValue, value, onChange);

  const formatDateTime = React.useCallback((d: Date | undefined): string => {
    if (!d) return '';
    return dayjs(d).format('YYYY-MM-DD HH:mm:ss');
  }, []);

  const [inputValue, setInputValue] = React.useState(formatDateTime(date));

  const isDateDisabled = React.useCallback(
    (d: Date) => {
      if (!disabled) return false;
      if (typeof disabled === 'function') return disabled(d);
      if (Array.isArray(disabled)) {
        const ts = d.valueOf();
        const set = new Set(disabled.map((x) => dayjs(x).valueOf()));
        return set.has(ts);
      }
      return false;
    },
    [disabled]
  );

  const parseInput = (val: string): Date | undefined => {
    if (!DateTimeRegex.test(val)) return undefined;
    const d = dayjs(val, 'YYYY-MM-DD HH:mm:ss');
    if (!d.isValid()) return undefined;
    const js = d.toDate();
    if (isDateDisabled(js)) return undefined;
    return js;
  };

  const handleInputChange = (val: string) => {
    setInputValue(val);
    if (val === '' || val === undefined) {
      setDate(undefined);
      return;
    }
    const parsed = parseInput(val);
    if (parsed) setDate(parsed);
  };

  const handleInputBlur = () => {
    const parsed = parseInput(inputValue);
    if (!parsed) setInputValue(formatDateTime(date));
  };

  React.useEffect(() => {
    setInputValue(formatDateTime(date));
  }, [date, formatDateTime]);

  const hours = React.useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  const minutes = React.useMemo(() => Array.from({ length: 60 }, (_, i) => i), []);
  const seconds = React.useMemo(() => Array.from({ length: 60 }, (_, i) => i), []);

  const current = date || new Date();
  const h = current.getHours();
  const m = current.getMinutes();
  const s = current.getSeconds();

  const update = (partial: { h?: number; m?: number; s?: number; d?: Date }) => {
    const base = partial.d || date || new Date();
    const next = new Date(base);
    next.setHours(partial.h !== undefined ? partial.h : h);
    next.setMinutes(partial.m !== undefined ? partial.m : m);
    next.setSeconds(partial.s !== undefined ? partial.s : s);
    if (isDateDisabled(next)) return;
    setDate(next);
  };

  const handleCalendarSelect = (d: Date | undefined) => {
    if (!clearable || d) {
      update({ d: d });
    }
    // 不自动关闭，允许继续选择时间
  };

  const btn = (
    <span
      id="date-time-picker"
      role="button"
      tabIndex={-1}
      className="h-6 w-6 -mr-1 text-muted-foreground hover:text-accent-foreground flex items-center justify-center rounded-md transition hover:shadow-xs hover:bg-accent select-none outline-none"
      aria-label="Select date time"
    >
      <CalendarIcon className="size-4" />
      <span className="sr-only">Select date time</span>
    </span>
  );

  const shouldClear = clearable && !!inputValue;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <Input
          value={inputValue}
          placeholder={placeholder || '请选择日期时间'}
          suffix={shouldClear ? undefined : btn}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
            }
          }}
          onValueChange={handleInputChange}
          className={cn('pr-9', className)}
          onBlur={handleInputBlur}
          clearable={shouldClear}
          disabled={disabled === true}
        />
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-auto overflow-hidden p-0"
        align="end"
        alignOffset={-8}
        sideOffset={10}
      >
        <div className="flex h-80">
          <Calendar
            mode="single"
            selected={date}
            captionLayout={captionLayout}
            onSelect={handleCalendarSelect}
            locale={zhCN}
            disabled={disabled}
          />
          <div className="flex border-l border-border">
            <TimeColumn
              open={open}
              label="时"
              values={hours}
              selected={h}
              onSelect={(v) => update({ h: v })}
            />
            <TimeColumn
              open={open}
              label="分"
              values={minutes}
              selected={m}
              onSelect={(v) => update({ m: v })}
            />
            <TimeColumn
              open={open}
              label="秒"
              values={seconds}
              selected={s}
              onSelect={(v) => update({ s: v })}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-2 border-t border-border">
          <button
            type="button"
            className="px-2 py-1 text-xs rounded hover:bg-accent"
            onClick={() => setOpen(false)}
          >
            确定
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface TimeColumnProps {
  label: string;
  values: number[];
  selected: number;
  onSelect: (val: number) => void;
  open?: boolean;
}

function TimeColumn({ label, values, selected, onSelect, open }: TimeColumnProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  // 当弹层打开或选中值变化时让当前项居中
  React.useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;
    const activeBtn = container.querySelector('button[data-active="true"]');
    if (activeBtn) {
      activeBtn.scrollIntoView({ block: 'center' });
    }
  }, [open, selected]);

  return (
    <div className="flex flex-col w-16">
      <div className="text-center py-1 text-xs border-b border-border select-none">{label}</div>
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto h-[224px] scrollbar-custom scroll-snap-y scroll-snap-mandatory"
      >
        <ul className="m-0 p-0 list-none">
          {values.map((v) => {
            const active = v === selected;
            return (
              <li key={v} className="p-0 m-0 scroll-snap-center">
                <button
                  type="button"
                  data-active={active ? 'true' : 'false'}
                  className={cn(
                    'w-full px-2 py-1 text-left text-xs rounded-sm transition',
                    active ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                  )}
                  onClick={() => onSelect(v)}
                >
                  {String(v).padStart(2, '0')}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
